import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';

const ADMIN_ROLES = ['superintendent', 'principal', 'accountant', 'librarian', 'receptionist', 'lab_attendant'];

const ROLE_META = {
  superintendent: { label: 'Superintendent', icon: 'fa-user-tie',      color: 'text-purple-700', bg: 'bg-purple-100' },
  principal:      { label: 'Principal',      icon: 'fa-user-graduate', color: 'text-blue-700',   bg: 'bg-blue-100' },
  accountant:     { label: 'Accountant',     icon: 'fa-coins',         color: 'text-indigo-700', bg: 'bg-indigo-100' },
  librarian:      { label: 'Librarian',      icon: 'fa-book',          color: 'text-pink-700',   bg: 'bg-pink-100' },
  receptionist:   { label: 'Receptionist',   icon: 'fa-headset',       color: 'text-teal-700',   bg: 'bg-teal-100' },
  lab_attendant:  { label: 'Lab Attendant',  icon: 'fa-flask',         color: 'text-cyan-700',   bg: 'bg-cyan-100' },
};

export default function AdministrativeStaff() {
  const { user: currentUser } = useAuth();
  const role = currentUser?.role;
  const canEdit = ['admin', 'superintendent', 'principal'].includes(role);

  const [staff, setStaff] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [campusFilter, setCampusFilter] = useState('all');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '',
    phone: '', address: '', cnic: '',
    role: 'accountant', campus_id: '',
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get('/users/'),
        api.get('/campuses/').catch(() => ({ data: [] })),
      ]);
      setStaff((u.data || []).filter(x => ADMIN_ROLES.includes(x.role)));
      setCampuses(c.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getCampusName = (id) => {
    const c = campuses.find(x => x.id === id);
    return c ? c.name : '—';
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setError('');

    if (!form.first_name || !form.last_name) {
      setError('First and last name are required.'); return;
    }
    if (!editing && (!form.email || form.password.length < 8)) {
      setError('Email is required and password must be at least 8 characters.'); return;
    }

    setBusy(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          address: form.address,
        });
      } else {
        await api.post('/users/', {
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone || '',
          address: form.address || '',
          cnic: form.cnic || null,
          role: form.role,
        });
      }
      setModalOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setForm({
      first_name: '', last_name: '', email: '', password: '',
      phone: '', address: '', cnic: '',
      role: 'accountant', campus_id: '',
    });
    setEditing(null);
    setError('');
  };

  const openEdit = (person) => {
    setEditing(person);
    setForm({
      first_name: person.first_name,
      last_name: person.last_name,
      email: person.email,
      password: '',
      phone: person.phone || '',
      address: person.address || '',
      cnic: person.cnic || '',
      role: person.role,
      campus_id: person.campus_id || '',
    });
    setModalOpen(true);
  };

  const toggleStatus = async (person) => {
    try {
      await api.put(`/users/${person.id}`, { is_active: !person.is_active });
      fetchAll();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ─── Filter ───
  const filtered = staff.filter(s => {
    if (roleFilter !== 'all' && s.role !== roleFilter) return false;
    if (campusFilter !== 'all') {
      const campusName = campuses.find(c => c.id === parseInt(campusFilter))?.name;
      if (s.campus_name !== campusName) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const blob = `${s.first_name} ${s.last_name} ${s.email} ${s.cnic || ''} ${s.phone || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  // ─── Counts per role ───
  const countsByRole = ADMIN_ROLES.reduce((acc, r) => {
    acc[r] = staff.filter(s => s.role === r).length;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">📋 Administrative Staff</h2>
        <div className="flex gap-2">
          <button onClick={fetchAll} disabled={loading}
            className="bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200 text-sm">
            <i className="fas fa-sync-alt mr-1"></i> Refresh
          </button>
          {canEdit && (
            <button onClick={() => { resetForm(); setModalOpen(true); }}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
              <i className="fas fa-plus mr-1"></i> Add Staff
            </button>
          )}
        </div>
      </div>

      {/* ══════ Clickable role cards ══════ */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
        <button type="button"
          onClick={() => setRoleFilter('all')}
          title="Show all admin staff"
          className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
            roleFilter === 'all'
              ? 'border-gray-700 ring-2 ring-gray-300'
              : 'border-gray-300 hover:border-gray-500'
          }`}>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <i className="fas fa-users text-gray-700"></i> All
          </p>
          <p className="text-2xl font-bold text-gray-800">{staff.length}</p>
        </button>

        {ADMIN_ROLES.map(r => {
          const meta = ROLE_META[r];
          const isActive = roleFilter === r;
          return (
            <button key={r} type="button"
              onClick={() => setRoleFilter(prev => prev === r ? 'all' : r)}
              title={isActive ? 'Click to clear filter' : `Filter by ${meta.label}`}
              className={`text-left bg-white p-3 rounded shadow border-l-4 transition-all ${
                isActive
                  ? `ring-2 ring-offset-1 ring-gray-300 ${meta.bg} border-transparent`
                  : 'border-gray-300 hover:border-gray-500'
              }`}>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <i className={`fas ${meta.icon} ${meta.color}`}></i> {meta.label}
              </p>
              <p className={`text-2xl font-bold ${meta.color}`}>{countsByRole[r] || 0}</p>
            </button>
          );
        })}
      </div>

      {/* ══════ Filter bar ══════ */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select className="p-2 border rounded text-sm" value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}>
          <option value="all">All Roles</option>
          {ADMIN_ROLES.map(r => <option key={r} value={r}>{ROLE_META[r].label}</option>)}
        </select>

        <select className="p-2 border rounded text-sm" value={campusFilter}
          onChange={e => setCampusFilter(e.target.value)}>
          <option value="all">All Campuses</option>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <input type="text" placeholder="Search by name, email, phone, CNIC..."
          className="flex-1 min-w-[200px] p-2 border rounded text-sm"
          value={search} onChange={e => setSearch(e.target.value)} />

        <span className="ml-auto text-sm text-gray-500">
          Showing {filtered.length} of {staff.length}
        </span>
      </div>

      {/* ══════ Table ══════ */}
      <div className="bg-white p-4 rounded shadow">
        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-user-tie text-4xl mb-3"></i>
            <p>No admin staff found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Name', 'Role', 'Email', 'Phone', 'CNIC', 'Campus', 'Status', 'Actions'].map(h => (
                    <th key={h} className="p-3 text-left font-semibold text-gray-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const meta = ROLE_META[s.role];
                  return (
                    <tr key={s.id} className="border-t hover:bg-gray-50">
                      <td className="p-3 font-medium">{s.first_name} {s.last_name}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${meta?.bg || 'bg-gray-100'} ${meta?.color || 'text-gray-700'}`}>
                          <i className={`fas ${meta?.icon || 'fa-user'} mr-1`}></i>
                          {meta?.label || s.role}
                        </span>
                      </td>
                      <td className="p-3 text-gray-600">{s.email}</td>
                      <td className="p-3">{s.phone || '—'}</td>
                      <td className="p-3 font-mono text-xs">{s.cnic || '—'}</td>
                      <td className="p-3">{s.campus_name || getCampusName(s.campus_id)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {canEdit && (
                            <>
                              <button onClick={() => openEdit(s)}
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                                title="Edit">
                                <i className="fas fa-edit"></i>
                              </button>
                              {currentUser?.role === 'admin' && (
                                <button onClick={() => toggleStatus(s)}
                                  disabled={s.id === currentUser?.id}
                                  className={`px-2 py-1 text-xs rounded ${
                                    s.id === currentUser?.id
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : s.is_active
                                        ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                                  }`}
                                  title={s.id === currentUser?.id ? "You can't deactivate yourself" : ""}>
                                  {s.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════ Add / Edit Modal ══════ */}
      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); resetForm(); }}
        title={editing ? 'Edit Admin Staff' : 'Add Admin Staff'}
        onSave={handleSubmit}
        saveLabel={editing ? 'Update' : 'Create'}
        size="lg"
      >
        <div className="space-y-3">
          {error && <div className="bg-red-50 text-red-600 p-2 rounded text-sm">{error}</div>}

          {!editing && (
            <div>
              <label className="block text-sm font-medium mb-1">Role *</label>
              <select className="w-full p-2 border rounded"
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}>
                {ADMIN_ROLES.map(r => (
                  <option key={r} value={r}>{ROLE_META[r].label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <input className="p-2 border rounded" placeholder="First Name *"
              value={form.first_name}
              onChange={e => setForm({ ...form, first_name: e.target.value })} />
            <input className="p-2 border rounded" placeholder="Last Name *"
              value={form.last_name}
              onChange={e => setForm({ ...form, last_name: e.target.value })} />
          </div>

          <input className="w-full p-2 border rounded" type="email" placeholder="Email *"
            value={form.email} disabled={!!editing}
            onChange={e => setForm({ ...form, email: e.target.value })} />

          {!editing && (
            <input className="w-full p-2 border rounded" type="password"
              placeholder="Password (min 8 chars) *"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })} />
          )}

          <div className="grid grid-cols-2 gap-3">
            <input className="p-2 border rounded" placeholder="Phone"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} />
            <input className="p-2 border rounded" placeholder="CNIC"
              value={form.cnic}
              onChange={e => setForm({ ...form, cnic: e.target.value })} />
          </div>

          <input className="w-full p-2 border rounded" placeholder="Address"
            value={form.address}
            onChange={e => setForm({ ...form, address: e.target.value })} />

          <p className="text-xs text-gray-500 italic">
            💡 After creating, you can also assign them via <strong>User Management</strong> to change role or deactivate.
          </p>
        </div>
      </Modal>
    </div>
  );
}